import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material"
import React, { useContext, useEffect, useState } from "react";
import propTypes from "prop-types";
import { api, Device, DeviceConfig, IListDeviceConfigsRequest, IGenerateActivationResponse } from "../../../logic/api";
import { PagedDataView } from "../../../components/lists/PagedDataView/PagedDataView";
import DeviceConfigListItem from "../devices_admin/components/listItems/DeviceConfigListItem";
import { SessionContext } from "../../../utility/useSession";
import { ProgressDialog } from "../../../components/notifications/progressDialog/progressDialog";
import ErrorDialog from "../../../components/notifications/errorDialog/ErrorDialog";
import flex from "../../../styles/flex";
import { FiCheckCircle } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import QRCode from "react-qr-code";

export const DeviceActivationDialog: React.FunctionComponent<{
    show: boolean;
    deviceConfig?: DeviceConfig | undefined,
    onCompleted: (didActivateNewDevice: boolean) => void;
}> = (props) => {
    const session = useContext(SessionContext);
    const { t } = useTranslation();
    const [deviceConfigListParams] = useState<IListDeviceConfigsRequest>({ includeInactive: false, paging: { limit: 0, offset: 0 } });
    const [deviceConfig, setDeviceConfig] = useState<DeviceConfig | undefined>(props.deviceConfig);
    const [isCancellingActivation, setIsCancellingActivation] = useState(false);
    const [isCheckingActivation, setIsCheckingActivation] = useState(false);
    const [device, setDevice] = useState<Device | null>(null);
    const [activationCheckIndex, setActivationCheckIndex] = useState(0);
    const [deviceIndex, setDeviceIndex] = useState(0);
    const requestActivationCode = api.hooks.deviceManager.generateActivation();
    const cancelActivation = api.hooks.deviceManager.cancelActivation();
    const checkActivationState = api.hooks.deviceManager.checkActivationState();
    const [activationInfo, setActivationInfo] = useState<IGenerateActivationResponse | null>(null);
    const [activationError, setActivationError] = useState<string | null>();

    const setupNextDevice = () => {
        setActivationError(null);
        setIsCheckingActivation(false);
        setIsCancellingActivation(false);
        setActivationCheckIndex(0);
        setActivationInfo(null);
        setDevice(null);
        setDeviceIndex(deviceIndex + 1);
    }

    const activatedStateInError = (err: string) => {
        setActivationError(err);
        setIsCheckingActivation(false);
        setIsCancellingActivation(false);
        setActivationInfo(null);
        setDevice(null);
    }

    useEffect(() => {
        if (props.show) {
            setActivationError(null);
            setIsCheckingActivation(false);
            setIsCancellingActivation(false);
            setActivationCheckIndex(0);
            setActivationInfo(null);
            setDevice(null);
        } else {
            setIsCheckingActivation(false);
            setIsCancellingActivation(false);
            setActivationCheckIndex(0);
            setDeviceConfig(undefined);
            setActivationInfo(null);
            setDevice(null);
        }
    }, [props.deviceConfig, props.show]);

    useEffect(() => {
        const deviceConfigId = deviceConfig?.id;
        if (props.show == false || deviceConfigId == null) return;
        setActivationInfo(null);
        requestActivationCode.send(session.current.context, {
            deviceConfigId: deviceConfigId,
        }, (result) => {
            setActivationInfo(result);
            setActivationCheckIndex(0);
            setIsCheckingActivation(true);
        });

    }, [deviceConfig, props.show, deviceIndex]);


    useEffect(() => {
        const activationId = activationInfo?.activationId;

        if (props.show && isCheckingActivation && activationId != null) {
            const timeout = setTimeout(() => {
                console.log("Checking for activation..", activationId);
                checkActivationState.send(session.current.context, {
                    activationId: activationId
                }, (result) => {
                    if (result != null && result.errorMessage != null) {
                        activatedStateInError(result.errorMessage);
                        return;
                    }
                    if (result != null && result.activated == true && result.device != null) {
                        setDevice(result.device);
                        setIsCheckingActivation(false);
                        setActivationCheckIndex(0);
                    } else {
                        setActivationCheckIndex(activationCheckIndex + 1);
                    }
                });
            }, 1000);
            return () => clearTimeout(timeout);
        }
    }, [isCheckingActivation, activationCheckIndex, activationInfo, props.show]);


    const close = () => {
        setIsCheckingActivation(false);
        const activationId = activationInfo?.activationId;
        setActivationInfo(null);
        if (activationId != null) {
            setIsCancellingActivation(true);
            cancelActivation.send(session.current.context, {
                activationId: activationId
            }, () => {
                setIsCancellingActivation(false);
                props.onCompleted(false);
            });
        } else {
            props.onCompleted(false);
        }

    }

    if (activationError != null) {
        return <Dialog open={props.show} fullWidth={true} maxWidth="sm" >
            <DialogTitle style={{ textAlign: "center" }}>
                <b>{t("mobile.activationFailed")}</b>
            </DialogTitle>
            <DialogContent>
                <div style={{ textAlign: "center", fontSize: 18 }}>
                    <div style={{ marginBottom: 100 }}>{activationError}</div>


                </div>

            </DialogContent>
            <DialogActions>


                <Button onClick={() => props.onCompleted(false)} color="secondary">{t("general.cancel")}</Button>

                <Button onClick={() => setupNextDevice()} color="secondary">{t("general.retry")}</Button>



            </DialogActions>
        </Dialog >
    }

    if (props.show == false) {
        return <></>;
    }
    if (deviceConfig == null) {
        return <Dialog open={props.show} fullWidth={true} maxWidth="sm" >
            <DialogTitle>
                {t("mobile.selectConfigNew")}
            </DialogTitle>
            <DialogContent>
                <PagedDataView<IListDeviceConfigsRequest, DeviceConfig>
                    apiHook={api.hooks.deviceManager.listConfigs}
                    parameters={deviceConfigListParams}
                    renderItem={(item) => {
                        return <DeviceConfigListItem
                            key={item.id}
                            deviceConfig={item}
                            onTap={() => {
                                setDeviceConfig(item);
                            }}
                        ></DeviceConfigListItem>
                    }}>
                    {t("mobile.noConfigFound")}
                </PagedDataView>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => props.onCompleted(false)} color="secondary">{t("general.cancel")}</Button>
            </DialogActions>
        </Dialog>
    }

    if (isCancellingActivation) {
        return <ProgressDialog status={cancelActivation.status} delay={0} showOnIdle={true}></ProgressDialog>
    }

    if (device != null) {
        return <Dialog open={props.show} fullWidth={true} maxWidth="sm" >
            <DialogTitle style={{ textAlign: "center" }}>
                <b>{t("mobile.activationCompleted")}</b>
            </DialogTitle>
            <DialogContent>
                <div style={{ textAlign: "center", fontSize: 18 }}>
                    <div style={{ marginBottom: 100 }}>{t("mobile.deviceActivated")}</div>

                    <FiCheckCircle style={{ marginBottom: 50 }} color="green" size={50}></FiCheckCircle>

                </div>

            </DialogContent>
            <DialogActions>
                <Button onClick={() => setupNextDevice()} color="secondary">{t("mobile.setupAnotherDevice")}</Button>

                <Button onClick={() => props.onCompleted(true)} color="secondary">{t("mobile.finished")}</Button>
            </DialogActions>
        </Dialog >
    }



    return <Dialog open={props.show} fullWidth={true} maxWidth="sm" >
        <DialogTitle style={{ textAlign: "center" }}>
            <b>{activationInfo?.name} - Activation</b>
        </DialogTitle>
        <DialogContent>
            <ErrorDialog err={requestActivationCode.err}></ErrorDialog>
            <ProgressDialog status={requestActivationCode.status} delay={0}>

            </ProgressDialog>

            {(activationInfo != null) && (
                <div style={{ textAlign: "center", fontSize: 18 }}>
                    <div>Scan the QR code below to download the app.</div>
                    <div style={{ paddingTop: 20 }}>Once the app is installed, scan the barcode again when prompted or enter the activation code below.</div>

                    <div style={{ paddingTop: 20 }}>You will be required to enable downloads from <b>Unknown Sources</b> on the mobile device settings</div>

                    <div style={{ paddingTop: 20, paddingBottom: 20, fontSize: 30 }}><b>{activationInfo?.code}</b></div>


                    <QRCode width={160} height={160} spacing={20} value={activationInfo.code} />


                    <div style={{ paddingTop: 20 }}>Activation PIN:</div>
                    <div style={{ paddingTop: 20, fontSize: 30 }}><b>{activationInfo?.pin}</b>
                    </div>
                </div>
            )}
            <div style={{ marginTop: 20 }}>
                <div style={flex.flexRows}>
                    <div style={flex.flexAuto}>
                        <CircularProgress size={15}></CircularProgress>
                    </div>
                    <div style={flex.flexFill}>
                        <div style={{ marginLeft: 10 }}>Waiting for activation..</div>
                    </div>
                </div>

            </div>

        </DialogContent>
        <DialogActions>
            {(activationInfo != null) && (
                <Button onClick={() => close()} color="secondary">Cancel</Button>
            )}
        </DialogActions>
    </Dialog>
};

DeviceActivationDialog.propTypes = {
    show: propTypes.bool.isRequired,
    deviceConfig: propTypes.any,
    onCompleted: propTypes.any.isRequired
};